import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Divider,
  FormControl,
  FormLabel,
  Heading,
  Select,
  Stack,
  Switch,
  Text,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';

import { MyProfileBadges } from '@/components/MyProfileBadges';
import { EAtMentionNotificationSetting, EUserRole } from '@/graphql-types/globalTypes';
import { Card } from '@/imports/ui/chakra/components/Card';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { OnboardingChecklist } from '@/imports/ui/components/OnboardingChecklist';
import { Hummus, HummusAside, HummusMain } from '@/layouts/Hummus';

import {
  MyProfileNotificationsPage_UpdateUser,
  MyProfileNotificationsPage_UpdateUserVariables,
} from './graphql-types/MyProfileNotificationsPage_UpdateUser';
import { MyProfileNotificationsPage_User } from './graphql-types/MyProfileNotificationsPage_User';

import { logError } from '../../imports/logging/ClientLogger';
import { Accessibility } from './components/Accessibility';
import { CompanyNotifications } from './components/CompanyNotifications/CompanyNotifications';
import { EventNotifications } from './components/EventNotifications/EventNotifications';
import { GroupNotifications } from './components/GroupNotifications/GroupNotifications';
import { MembershipNotifications } from './components/MembershipNotifications/MembershipNotifications';
import { UserNotifications } from './components/UserNotifications/UserNotifications';

const UPDATE_USER = gql`
  mutation MyProfileNotificationsPage_UpdateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      data {
        _id
        privateMessageNotification
        atMentionNotification
        eventConfirmationEmails
        eventReminderEmails
        eventSummaryEmails
        sponsoredEventEmails
        optOutAllCommunityEmails
        optOutMembershipRenewalReminderEmails
      }
    }
  }
`;

const GET_USER = gql`
  query MyProfileNotificationsPage_User {
    currentUser {
      _id
      optOutAllCommunityEmails
      role
      rosterOrganization {
        _id
        optOutMembershipRenewalReminderEmails
      }
      ...UserNotifications_User
      ...EventNotifications_User
      ...MembershipNotifications_User
    }
  }
  ${UserNotifications.userFragment}
  ${EventNotifications.userFragment}
  ${MembershipNotifications.userFragment}
`;

export const MyProfileNotificationsPage: React.FC = () => {
  const toast = useToast();
  const query = useQuery<MyProfileNotificationsPage_User>(GET_USER);

  const [updateUser, { loading }] = useMutation<
    MyProfileNotificationsPage_UpdateUser,
    MyProfileNotificationsPage_UpdateUserVariables
  >(UPDATE_USER, {
    refetchQueries: ['MyProfileNotificationsPage_User'],
    awaitRefetchQueries: true,
  });

  const currentUser = query.data?.currentUser;

  if (query.loading) {
    return <Loading />;
  }

  if (query.error) {
    return <UnexpectedError />;
  }

  if (!currentUser) {
    return <UnexpectedError />;
  }

  const userIsOptedOut = !!currentUser.optOutAllCommunityEmails;

  const onChange = async (data: {
    optOutAllCommunityEmails?: boolean;
    privateMessageNotification?: boolean;
    atMentionNotification?: EAtMentionNotificationSetting;
    eventConfirmationEmails?: boolean;
    eventReminderEmails?: boolean;
    eventSummaryEmails?: boolean;
    sponsoredEventEmails?: boolean;
    optOutMembershipRenewalReminderEmails?: boolean;
  }): Promise<void> => {
    try {
      await updateUser({
        variables: {
          input: {
            id: currentUser._id,
            data,
          },
        },
      });
      toast({ status: 'success', description: 'Updated preference' });
    } catch (err) {
      toast({ status: 'error', description: 'Failed to update preference.' });
      logError(err);
    }
  };

  return (
    <Hummus>
      <HummusMain>
        <Stack spacing="4">
          <Card px="4" py="6" bg="transparent" boxShadow="none" p="0" mb="10px">
            <Stack spacing="0">
              <Heading as="h4" size="md" mb="2">
                Notifications
              </Heading>
              <Text color="#64748B">
                Choose how often you receive emails from the community
              </Text>
            </Stack>
          </Card>
          <Card p="4">
            <FormControl
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              w="full"
            >
              <FormLabel mb="0">Opt Out of All Emails</FormLabel>
              <Switch
                isChecked={currentUser.optOutAllCommunityEmails === true}
                onChange={(event) =>
                  onChange({
                    optOutAllCommunityEmails: event.target.checked,
                  })
                }
              />
            </FormControl>
          </Card>
          <UserNotifications
            user={currentUser}
            isDisabled={userIsOptedOut || loading}
            onChange={onChange}
          />
          <EventNotifications
            user={currentUser}
            isDisabled={userIsOptedOut || loading}
            onChange={onChange}
          />
          <GroupNotifications isDisabled={userIsOptedOut} />
          <CompanyNotifications
            currentUserId={currentUser._id}
            isDisabled={userIsOptedOut}
          />
          <MembershipNotifications
            user={currentUser}
            onChange={onChange}
            isAccountOwner={
              currentUser.role === EUserRole.ORGANIZATION_ACCOUNT_OWNER
            }
            rosterOrganization={currentUser.rosterOrganization ?? null}
          />

          <Accessibility />
        </Stack>
      </HummusMain>
      <HummusAside>
        <Stack spacing="4">
          <OnboardingChecklist />
          <MyProfileBadges />
        </Stack>
      </HummusAside>
    </Hummus>
  );
};
